// Perguntas da visita: uma pergunta por tela, e a resposta gravada.
//
// Pedido do Ilton (27/09): ler a pergunta, ver ao lado a resposta que faz
// sentido e as que soam bem mas não estão certas, e — "na hora que ela estiver
// fazendo a resposta, eu vou estar com o celular na mão" — gravar. Um toque em
// "Respondeu", "Ficou vago" ou "Pulei" para a gravação, marca e passa para a
// próxima: a mão não sai do celular e o olho não sai da médica.
//
// O áudio fica guardado no celular até o servidor confirmar (gravacoesPendentes)
// e o servidor transcreve sozinho. Ouvir, só o admin.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ROTULO_MARCA, ehPergunta, formatarData, formatarHora, podeEscrever,
  type GravacaoDTO, type MarcaDaPergunta, type Perfil, type ResumoRoteiroDTO, type RoteiroDTO,
} from '@medicov1/shared';
import { chamar } from '../api';
import { Ditar } from '../componentes/Ditar';
import { Gravador, formatarDuracao, gravacaoDisponivel } from '../dados/gravador';
import { enviarTodas, guardarPendente, listarPendentes, type GravacaoPendente } from '../dados/gravacoesPendentes';
import './perguntas.css';

type Aba = 'certa' | 'cuidado' | 'anotar';
type RoteiroTela = RoteiroDTO & { transcricaoAutomatica?: boolean };

const ROTULO_SITUACAO: Record<GravacaoDTO['situacao'], string> = {
  pendente: 'aguardando transcrição',
  transcrevendo: 'transcrevendo…',
  transcrita: 'transcrita',
  erro: 'transcrição falhou',
};

const BASE_API = import.meta.env.VITE_API_URL ?? '/api';

interface Props {
  pacienteId: string;
  perfil: Perfil;
  aoVoltar: () => void;
}

export function Perguntas({ pacienteId, perfil, aoVoltar }: Props) {
  const registra = podeEscrever(perfil);
  const [lista, setLista] = useState<ResumoRoteiroDTO[] | null>(null);
  const [roteiro, setRoteiro] = useState<RoteiroTela | null>(null);
  const [passo, setPasso] = useState(0);
  const [aba, setAba] = useState<Record<number, Aba>>({});
  const [notas, setNotas] = useState<Record<number, string>>({});
  const [pendentes, setPendentes] = useState<GravacaoPendente[]>([]);
  const [gravandoDesde, setGravandoDesde] = useState<number | null>(null);
  const [agora, setAgora] = useState(Date.now());
  const [erro, setErro] = useState('');
  const gravador = useRef<Gravador | null>(null);
  // Espelhos para o que roda fora do ciclo da tela (sair gravando).
  const roteiroRef = useRef<RoteiroTela | null>(null);
  const passoRef = useRef(0);
  roteiroRef.current = roteiro;
  passoRef.current = passo;

  // ------------------------------------------------------------ dados
  const abrirRoteiro = useCallback(async (id: string, manterPasso = false) => {
    try {
      const r = await chamar<RoteiroTela>(`/roteiros/${id}`);
      setRoteiro(r);
      setNotas(Object.fromEntries(Object.entries(r.marcas).map(([i, m]) => [i, m.nota ?? ''])));
      if (!manterPasso) setPasso(0);
      setPendentes(await listarPendentes(id));
      setErro('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui abrir o roteiro');
    }
  }, []);

  useEffect(() => {
    chamar<ResumoRoteiroDTO[]>(`/roteiros?pacienteId=${pacienteId}`)
      .then((l) => {
        setLista(l);
        // Um roteiro ativo só: abre direto — é para isso que se tocou no botão.
        const ativos = l.filter((x) => x.ativo);
        if (ativos.length === 1) void abrirRoteiro(ativos[0]!.id);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'não consegui listar os roteiros'));
  }, [pacienteId, abrirRoteiro]);

  // O que ficou guardado no celular sobe quando a internet voltar.
  const reenviar = useCallback(async () => {
    if (!roteiro) return;
    const n = await enviarTodas(roteiro.id);
    setPendentes(await listarPendentes(roteiro.id));
    if (n > 0) await abrirRoteiro(roteiro.id, true);
  }, [roteiro, abrirRoteiro]);

  useEffect(() => {
    const aoVoltarRede = () => { void reenviar(); };
    window.addEventListener('online', aoVoltarRede);
    return () => window.removeEventListener('online', aoVoltarRede);
  }, [reenviar]);

  // Enquanto alguma transcrição está a caminho, a tela vai buscando o texto.
  const esperando = Boolean(roteiro?.transcricaoAutomatica && roteiro.gravacoes.some((g) => g.situacao === 'pendente' || g.situacao === 'transcrevendo'));
  useEffect(() => {
    if (!roteiro || (!esperando && pendentes.length === 0)) return;
    const t = setInterval(() => { void reenviar(); if (esperando) void abrirRoteiro(roteiro.id, true); }, 8000);
    return () => clearInterval(t);
  }, [roteiro, esperando, pendentes.length, reenviar, abrirRoteiro]);

  // Relógio da gravação.
  useEffect(() => {
    if (gravandoDesde === null) return;
    const t = setInterval(() => setAgora(Date.now()), 500);
    return () => clearInterval(t);
  }, [gravandoDesde]);

  // A tela não apaga no meio da visita (quando o aparelho deixa).
  useEffect(() => {
    let trava: { release: () => Promise<void> } | null = null;
    const pedir = async () => {
      try {
        const wl = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock;
        if (wl && document.visibilityState === 'visible') trava = await wl.request('screen');
      } catch { /* sem trava, a tela apaga como sempre */ }
    };
    void pedir();
    document.addEventListener('visibilitychange', pedir);
    return () => { document.removeEventListener('visibilitychange', pedir); void trava?.release().catch(() => {}); };
  }, []);

  // Sair da tela gravando: guarda o que foi gravado em vez de perder.
  useEffect(() => () => { void pararEGuardar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------- gravação
  async function iniciarGravacao() {
    setErro('');
    try {
      gravador.current ??= new Gravador();
      await gravador.current.iniciar();
      setGravandoDesde(Date.now());
      setAgora(Date.now());
    } catch (e) {
      const nome = e instanceof DOMException ? e.name : '';
      setErro(nome === 'NotAllowedError'
        ? 'O celular não deixou usar o microfone. Libere o microfone para este site nas configurações do navegador e tente de novo.'
        : 'Não consegui ligar o microfone.');
      setGravandoDesde(null);
    }
  }

  /** Para a gravação (se houver), guarda no celular e tenta enviar. */
  async function pararEGuardar(): Promise<void> {
    const g = gravador.current;
    const r = roteiroRef.current;
    if (!g?.gravando || !r) { setGravandoDesde(null); return; }
    const indice = passoRef.current;
    const audio = await g.parar();
    setGravandoDesde(null);
    if (!audio) return;
    const pendente: GravacaoPendente = {
      idCliente: crypto.randomUUID(), roteiroId: r.id, indice,
      blob: audio.blob, tipoMime: audio.tipoMime, duracaoSeg: audio.duracaoSeg, gravadoEm: audio.gravadoEm, tentativas: 0,
    };
    await guardarPendente(pendente);
    setPendentes(await listarPendentes(r.id));
    await enviarTodas(r.id);
    setPendentes(await listarPendentes(r.id));
    if (roteiroRef.current?.id === r.id) void abrirRoteiro(r.id, true);
  }

  // ------------------------------------------------------ marcar e anotar
  async function salvarMarca(indice: number, campos: { marca?: MarcaDaPergunta | null; nota?: string | null }) {
    if (!roteiro) return;
    try {
      const m = await chamar<{ indice: number; marca: MarcaDaPergunta | null; nota: string | null }>(
        `/roteiros/${roteiro.id}/marcas/${indice}`, { metodo: 'PUT', corpo: campos },
      );
      setRoteiro((r) => (r ? { ...r, marcas: { ...r.marcas, [indice]: { marca: m.marca, nota: m.nota } } } : r));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui guardar a marca');
    }
  }

  async function marcarEAvancar(marca: MarcaDaPergunta) {
    const indice = passo;
    await pararEGuardar();
    void salvarMarca(indice, { marca, ...(notas[indice] !== undefined ? { nota: notas[indice] || null } : {}) });
    irPara(indice + 1);
  }

  async function irPara(n: number) {
    if (gravador.current?.gravando) await pararEGuardar();
    if (!roteiro) return;
    setPasso(Math.max(0, Math.min(roteiro.perguntas.length, n)));
    window.scrollTo({ top: 0 });
  }

  async function transcreverDeNovo(id: string) {
    try {
      await chamar(`/roteiros/gravacoes/${id}/transcrever`, { metodo: 'POST' });
      if (roteiro) await abrirRoteiro(roteiro.id, true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui pedir a transcrição');
    }
  }

  // ------------------------------------------------------------ desenho
  if (!roteiro) {
    return (
      <main className="pv">
        <header className="pv__topo">
          <div><h1>Perguntas da visita</h1><p>Roteiros de perguntas sobre os exames, para fazer à equipe.</p></div>
          <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
        </header>
        {erro && <p className="aviso-erro">{erro}</p>}
        {lista === null ? <p className="pv__dica">carregando…</p> : lista.length === 0 ? (
          <p className="pv__dica">Nenhum roteiro ainda. Eles são montados a partir dos exames, antes da visita.</p>
        ) : (
          <div className="pv__lista">
            {lista.map((r) => (
              <button key={r.id} className={`pv__roteiro${r.ativo ? '' : ' pv__roteiro--velho'}`} onClick={() => void abrirRoteiro(r.id)}>
                <b>{r.titulo}</b>
                <span>visita de {formatarData(r.paraDia)} · {r.marcadas} de {r.perguntas} perguntas marcadas{r.gravacoes ? ` · ${r.gravacoes} gravações` : ''}</span>
              </button>
            ))}
          </div>
        )}
      </main>
    );
  }

  const total = roteiro.perguntas.length;
  const noResumo = passo >= total;
  const p = roteiro.perguntas[passo];
  const marcaDe = (i: number) => roteiro.marcas[i]?.marca ?? null;
  const gravando = gravandoDesde !== null;
  const abaAqui = aba[passo] ?? 'certa';
  const gravacoesAqui = roteiro.gravacoes.filter((g) => g.indice === passo);
  const pendentesAqui = pendentes.filter((g) => g.indice === passo);

  return (
    <main className="pv pv--roteiro">
      <header className="pv__barra">
        <button className="botao botao--voltar" onClick={() => { void pararEGuardar(); setRoteiro(null); }}>Roteiros</button>
        {/* Andar entre as perguntas fica no alto: embaixo, só gravar e marcar. */}
        <div className="pv__andar">
          <button onClick={() => void irPara(passo - 1)} disabled={passo === 0} aria-label="Pergunta anterior">‹</button>
          <span className="pv__conta">{noResumo ? 'resumo' : `${passo + 1} de ${total}`}</span>
          <button onClick={() => void irPara(passo + 1)} disabled={noResumo} aria-label="Pular para a próxima">›</button>
          <button className="pv__andar__resumo" onClick={() => void irPara(total)} disabled={noResumo}>Resumo</button>
        </div>
      </header>
      <nav className="pv__trilha" style={{ ['--n' as string]: total }} aria-label="Perguntas">
        {roteiro.perguntas.map((q, i) => (
          <button key={i} aria-label={q.tema}
            className={[marcaDe(i) ?? '', i === passo ? 'agora' : ''].join(' ').trim()}
            onClick={() => void irPara(i)} />
        ))}
      </nav>

      {erro && <p className="aviso-erro">{erro}</p>}

      {noResumo ? (
        <section className="pv__resumo">
          <h2>{roteiro.titulo}</h2>
          <p className="pv__dica">Visita de {formatarData(roteiro.paraDia)}. Toque numa barra lá em cima para voltar a qualquer pergunta.</p>
          {roteiro.perguntas.map((q, i) => ehPergunta(q) && (
            <div key={i} className="pv__resumo__item">
              <span className={`pv__selo pv__selo--${marcaDe(i) ?? 'nada'}`}>{marcaDe(i) ? ROTULO_MARCA[marcaDe(i)!] : 'sem marca'}</span>
              <b>{q.tema}</b>
              {roteiro.marcas[i]?.nota && <p className="pv__resumo__nota">{roteiro.marcas[i]!.nota}</p>}
              {roteiro.gravacoes.filter((g) => g.indice === i).map((g) => (
                <p key={g.id} className="pv__resumo__fala">
                  {g.transcricao ? `“${g.transcricao}”` : `gravação de ${formatarHora(g.gravadoEm)} — ${ROTULO_SITUACAO[g.situacao]}`}
                </p>
              ))}
            </div>
          ))}
          <button className="botao botao--fantasma" onClick={() => void irPara(total - 1)}>Voltar à última pergunta</button>
        </section>
      ) : p && (
        <>
          <div className="pv__tema">{p.tema}</div>
          <section className="pv__fala">
            <span className="pv__rotulo">Pergunte</span>
            <p>{p.fala}</p>
            {p.numeros && p.numeros.length > 0 && (
              <div className="pv__numeros">{p.numeros.map((n) => <span key={n}>{n}</span>)}</div>
            )}
          </section>

          {!ehPergunta(p) ? (
            p.dica && <p className="pv__dica">{p.dica}</p>
          ) : (
            <>
              <div className="pv__abas" role="tablist">
                {([['certa', 'Resposta certa'], ['cuidado', 'Cuidado com'], ['anotar', notas[passo] ? 'Anotar ✎' : 'Anotar']] as [Aba, string][]).map(([k, rotulo]) => (
                  <button key={k} role="tab" aria-selected={abaAqui === k} className={`pv__aba pv__aba--${k}`}
                    onClick={() => setAba((a) => ({ ...a, [passo]: k }))}>{rotulo}</button>
                ))}
              </div>

              {abaAqui === 'certa' && (
                <section className="pv__painel pv__painel--certa"><ul>{(p.certa ?? []).map((c) => <li key={c}>{c}</li>)}</ul></section>
              )}
              {abaAqui === 'cuidado' && (
                <section className="pv__painel pv__painel--cuidado">
                  {(p.cuidado ?? []).map(([frase, porque]) => (
                    <div key={frase} className="pv__errada"><b>{frase}</b><span>Por quê: {porque}</span></div>
                  ))}
                  <p className="pv__dica">Se ouvir algo assim, sem confrontar: “A senhora pode me explicar melhor? Eu tinha lido que…”</p>
                </section>
              )}
              {abaAqui === 'anotar' && (
                <section className="pv__painel pv__painel--anotar campo">
                  <label htmlFor={`pv-nota-${passo}`}>O que ela respondeu</label>
                  <textarea id={`pv-nota-${passo}`} rows={4} value={notas[passo] ?? ''} disabled={!registra}
                    placeholder="Ex.: vai repetir o sódio amanhã cedo"
                    onChange={(e) => setNotas((n) => ({ ...n, [passo]: e.target.value }))}
                    onBlur={() => void salvarMarca(passo, { nota: notas[passo] || null })} />
                  {registra && (
                    <Ditar valor={notas[passo] ?? ''} rotulo="ditar a anotação"
                      aoMudar={(v) => { setNotas((n) => ({ ...n, [passo]: v })); void salvarMarca(passo, { nota: v || null }); }} />
                  )}
                </section>
              )}

              {/* As gravações desta pergunta: o que já subiu e o que está no celular. */}
              {(gravacoesAqui.length > 0 || pendentesAqui.length > 0) && (
                <section className="pv__gravacoes">
                  {pendentesAqui.map((g) => (
                    <div key={g.idCliente} className="pv__gravacao pv__gravacao--local">
                      <span>{formatarHora(g.gravadoEm)} · {formatarDuracao(g.duracaoSeg)}</span>
                      <small>guardada no celular — {g.ultimoErro ?? 'enviando…'}</small>
                    </div>
                  ))}
                  {gravacoesAqui.map((g) => (
                    <div key={g.id} className={`pv__gravacao pv__gravacao--${g.situacao}`}>
                      <span>{formatarHora(g.gravadoEm)}{g.duracaoSeg ? ` · ${formatarDuracao(g.duracaoSeg)}` : ''} · {ROTULO_SITUACAO[g.situacao]}</span>
                      {g.transcricao && <p>“{g.transcricao}”</p>}
                      {g.analise && <p className="pv__analise">{g.analise}</p>}
                      {roteiro.podeOuvir && <audio controls preload="none" src={`${BASE_API}/roteiros/gravacoes/${g.id}/audio`} />}
                      {roteiro.podeOuvir && g.situacao === 'erro' && (
                        <button className="botao botao--fantasma" onClick={() => void transcreverDeNovo(g.id)}>Transcrever de novo</button>
                      )}
                    </div>
                  ))}
                </section>
              )}
            </>
          )}
        </>
      )}

      {/* A barra de baixo: gravar e marcar ficam no polegar. */}
      {!noResumo && p && (
        <footer className="pv__pe">
          {ehPergunta(p) && registra && (
            gravacaoDisponivel() ? (
              <button className={`pv__gravar${gravando ? ' pv__gravar--on' : ''}`}
                onClick={() => void (gravando ? pararEGuardar() : iniciarGravacao())}>
                {gravando ? <>■ Parar gravação · {formatarDuracao((agora - gravandoDesde!) / 1000)}</> : <>● Gravar a resposta</>}
              </button>
            ) : (
              <p className="pv__dica">Este navegador não grava áudio. Use o Safari (iPhone) ou o Chrome (Android).</p>
            )
          )}
          {ehPergunta(p) && registra ? (
            <div className="pv__marcas">
              {(['ok', 'vago', 'pulou'] as MarcaDaPergunta[]).map((m) => (
                <button key={m} className={`pv__marca pv__marca--${m}${marcaDe(passo) === m ? ' pv__marca--escolhida' : ''}`}
                  onClick={() => void marcarEAvancar(m)}>{ROTULO_MARCA[m]}</button>
              ))}
            </div>
          ) : (
            <button className="pv__seguir" onClick={() => void irPara(passo + 1)}>
              {passo === 0 ? 'Começar' : passo === total - 1 ? 'Terminar e ver o resumo' : 'Próxima'}
            </button>
          )}
        </footer>
      )}
    </main>
  );
}
